import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { useEffect, useMemo, useRef } from 'react';
import useMountedState from 'react-use/lib/useMountedState';
import { setBetSliderAmountOnly } from '../redux/actions/pokerActions';
import { useAppDispatch } from '../redux/store';
import { getNearestStepX, getSliderValueFromXPosition, getXPositionFromValue } from '../scenes/poker/pixiComponents/slider/sliderUtils';
import { BET_SLIDER_TRACK_COLOR, GAME_BUTTON_BACKGROUND_COLOR, GAME_BUTTON_BACKGROUND_HOVER_COLOR, GAME_BUTTON_BORDER_COLOR } from '../styles/colors';
const ALL_POINTER_EVENT_TYPES = ['pointermove', 'pointerup', 'pointercancel'];
// SINGLETON ALERT!
const ACTIVE_WINDOW_LISTENERS = {
    pointermove: [],
    pointerup: [],
    pointercancel: []
};
function addWindowEventListeners(listeners) {
    Object.entries(listeners).forEach(([pointerEventType, fn]) => {
        window.addEventListener(pointerEventType, fn);
        ACTIVE_WINDOW_LISTENERS[pointerEventType].push(fn);
    });
}
/**
 * To avoid the possibility that some event handlers might be added and never properly cleaned up, store and remove all
 * function references that were previously added to window for the bet slider.
 */
function removeWindowEventListeners() {
    ALL_POINTER_EVENT_TYPES.forEach((pointerEventType) => {
        // remove all functions from arrays, then remove each from listening on window
        const fns = ACTIVE_WINDOW_LISTENERS[pointerEventType].splice(0, ACTIVE_WINDOW_LISTENERS[pointerEventType].length);
        fns.forEach((fn) => {
            window.removeEventListener(pointerEventType, fn);
        });
    });
}
export const BUTTON_BORDER_WIDTH_PX = 2;
export const BUTTON_HIGHLIGHT_PADDING_PX = 4;
export const DomBetSlider = ({ bounds, buttonWidth, betSliderAmount, numValues, minValue, maxValue, increment }) => {
    const dispatch = useAppDispatch();
    const containerRef = useRef(null);
    const buttonRef = useRef(null);
    const activePointerId = useRef();
    /**
     * Use a ref (updated via an effect) to ensure that pointermove handler has access to up-to-date value.
     */
    const betSliderAmountRef = useRef(betSliderAmount);
    const isMounted = useMountedState();
    /**
     * Clean up any registered bet slider handlers on window when unmounted.
     */
    useEffect(() => {
        return () => {
            removeWindowEventListeners();
        };
    }, []);
    useEffect(() => {
        betSliderAmountRef.current = betSliderAmount;
        if (!buttonRef.current) {
            return;
        }
        buttonRef.current.style.left = `${getXPositionFromValue(betSliderAmount, bounds.w, buttonWidth, minValue, maxValue, increment)}px`;
    }, [betSliderAmount, bounds.w, buttonWidth, increment, maxValue, minValue]);
    const onPointerDown = (event) => {
        if (activePointerId.current !== undefined && activePointerId.current !== event.pointerId) {
            // already dragging a different pointer ID, so ignore any additional "touches"
            return;
        }
        activePointerId.current = event.pointerId;
        const onGlobalPointerMove = (event) => {
            event.preventDefault();
            if (event.pointerId !== activePointerId.current) {
                return;
            }
            if (!isMounted()) {
                removeWindowEventListeners();
                return;
            }
            if (!containerRef.current || !buttonRef.current) {
                return;
            }
            const containerRect = containerRef.current.getBoundingClientRect();
            const rawButtonLeftPx = event.clientX - containerRect.left - buttonWidth / 2;
            const maxX = containerRect.width - buttonWidth;
            const boundedRawButtonLeftPx = Math.min(Math.max(0, rawButtonLeftPx), maxX);
            const sliderButtonX = getNearestStepX(boundedRawButtonLeftPx, maxX, numValues);
            buttonRef.current.style.left = `${sliderButtonX}px`;
            const value = getSliderValueFromXPosition(sliderButtonX, bounds.w, buttonWidth, minValue, maxValue, increment);
            if (value !== betSliderAmountRef.current) {
                dispatch(setBetSliderAmountOnly(value));
            }
        };
        const onGlobalPointerUp = (event) => {
            if (event.pointerId !== activePointerId.current) {
                return;
            }
            activePointerId.current = undefined;
            removeWindowEventListeners();
        };
        addWindowEventListeners({
            pointermove: onGlobalPointerMove,
            pointerup: onGlobalPointerUp,
            pointercancel: onGlobalPointerUp
        });
    };
    const containerCss = useMemo(() => css `
      position: absolute;
      left: ${bounds.x}px;
      top: ${bounds.y}px;
      width: ${bounds.w}px;
      height: ${bounds.h}px;
    `, [bounds.h, bounds.w, bounds.x, bounds.y]);
    const trackCss = useMemo(() => {
        const trackHeightPx = Math.min(8, bounds.h / 5);
        return css `
      position: absolute;
      top: ${bounds.h / 2 - trackHeightPx / 2}px;
      left: ${buttonWidth / 4}px;
      width: ${bounds.w - buttonWidth / 2}px;
      height: ${trackHeightPx}px;
      background-color: ${BET_SLIDER_TRACK_COLOR};
      border-radius: ${trackHeightPx / 2}px;
    `;
    }, [bounds.h, bounds.w, buttonWidth]);
    const buttonBorderRadius = Math.min(buttonWidth, bounds.h) / 4;
    const buttonCss = useMemo(() => css `
      position: absolute;
      width: ${buttonWidth}px;
      height: ${bounds.h}px;
      background-color: ${GAME_BUTTON_BACKGROUND_COLOR};
      border: ${BUTTON_BORDER_WIDTH_PX}px solid ${GAME_BUTTON_BORDER_COLOR};
      border-radius: ${buttonBorderRadius}px;
      cursor: pointer;
      user-select: none;
      pointer-events: initial;
      touch-action: none;
      &:hover {
        background-color: ${GAME_BUTTON_BACKGROUND_HOVER_COLOR};
      }
    `, [bounds.h, buttonBorderRadius, buttonWidth]);
    const buttonHighlightCss = useMemo(() => css `
      position: absolute;
      width: ${Math.max(0, buttonWidth - 2 * BUTTON_BORDER_WIDTH_PX - 2 * BUTTON_HIGHLIGHT_PADDING_PX)}px;
      height: ${Math.max(0, bounds.h / 2 - BUTTON_HIGHLIGHT_PADDING_PX)}px;
      left: ${BUTTON_HIGHLIGHT_PADDING_PX}px;
      top: ${BUTTON_HIGHLIGHT_PADDING_PX}px;
      border-radius: ${Math.max(0, buttonBorderRadius - BUTTON_HIGHLIGHT_PADDING_PX)}px;
      background-color: white;
      opacity: 0.2; // TODO: Use a solid color for the highlight (which would need to be updated on hover)
    `, [bounds.h, buttonBorderRadius, buttonWidth]);
    return (_jsxs("div", { ref: containerRef, css: containerCss, children: [_jsx("div", { css: trackCss }), _jsx("div", { ref: buttonRef, css: buttonCss, onPointerDown: onPointerDown, title: 'Adjust Bet Amount', children: _jsx("div", { css: buttonHighlightCss }) })] }));
};
